import PropTypes from 'prop-types';
import {createSlide} from '../modules/Editor/components/create-slide';
import {getIdForNextElement} from '~/utils/common';
import {isDark} from '~/styles/utils/utils';
import {i18n} from '~/i18n';
import {
  PAGE_ID_SPEED_COURSE,
  PICTURESLIDES,
  PICTURESQUESTIONNAIRE,
  QUESTIONNAIRE,
  QUESTIONNAIREINPUT,
  CONTENT,
  TEXT,
  VIDEO,
  DILEMMA,
  PICTURE,
  TEMPLATE_DILEMMA,
  TEMPLATE_QUESTIONNAIRE,
  TEMPLATE_PICTURESLIDES,
  TEMPLATE_VIDEO,
  TEMPLATE_INPUTQUESTIONNAIRE,
  TEMPLATE_CONTENT,
  TEMPLATE_PICTUREQUESTIONNAIRE,
  EMBED,
  TEMPLATE_EMBED,
} from './constants';
import {staticsRoot} from '~/config';
import {loadableRetry} from '@snapper/utils';
import {migrateLegacyQuizData} from '~/modules/QuizView/EditorView/adapters/legacy-to-v2.adapter';
import {QuestionType} from '~/stores/rtk-query/types/quiz.types';

const PictureSlide = loadableRetry(() => {
  console.log('load PictureSlide');

  return import(/* webpackChunkName: "PictureSlide" */ '~/modules/Editor/components/PictureSlides/PictureSlide');
}, {name: 'PictureSlide'});

const PicturesQuestionnaire = loadableRetry(() => {
  console.log('load PicturesQuestionnaire');

  return import(/* webpackChunkName: "PicturesQuestionnaire" */ '~/modules/Player/components/PicturesQuestionnaire');
}, {name: 'PicturesQuestionnaire'});

const Questionnaire = loadableRetry(() => {
  console.log('load Questionnaire');

  return import(/* webpackChunkName: "Questionnaire" */ '~/modules/Player/components/Questionnaire');
}, {name: 'Questionnaire'});

const QuestionnaireInput = loadableRetry(() => {
  console.log('load QuestionnaireInput');

  return import(/* webpackChunkName: "QuestionnaireInput" */ '~/modules/Player/components/QuestionnaireInput');
}, {name: 'QuestionnaireInput'});

const Video = loadableRetry(() => {
  console.log('load Video');

  return import(/* webpackChunkName: "Video" */ '~/modules/Player/components/Video');
}, {name: 'Video'});

const Picture = loadableRetry(() => {
  console.log('load Picture');

  return import(/* webpackChunkName: "Picture" */ '~/modules/Player/components/Picture');
}, {name: 'Picture'});

const Dilemma = loadableRetry(() => {
  console.log('load Dilemma');

  return import(/* webpackChunkName: "Dilemma" */ '~/modules/Player/components/Dilemma');
}, {name: 'Dilemma'});

const Content = loadableRetry(() => {
  console.log('load Content');

  return import(/* webpackChunkName: "Content" */ '~/modules/Player/components/Content');
}, {name: 'Content'});

const Embed = loadableRetry(() => {
  console.log('load Embed');

  return import(/* webpackChunkName: "Embed" */ '~/modules/Player/components/Embed');
}, {name: 'Embed'});

const QuizView = loadableRetry(() => {
  console.log('load QuizView');

  return import(/* webpackChunkName: "QuizView" */ '~/modules/QuizView').then(m => ({'default': m.QuizView}));
}, {name: 'QuizView'});

export const pageTypes = [];

export const setPagesTypes = newPageTypes => {
  pageTypes.splice(0, pageTypes.length, ...newPageTypes || []);

  return pageTypes;
};

// export const getEmptyCourseData = () => ({
//   activePageId: PAGE_ID_DESCRIPTION,
//   description: '',
//   name: '',
//   pages: [],
//   publicationStatus: publicationStatus.PUBLICATION_STATUS_NOT_PUBLISHED,
// });

export const getPageSections = pageTypeId => {
  const pageType = pageTypes.find(ipageType => ipageType.id === pageTypeId);

  return pageType?.sections || [];
};

export const getParsedCourseData = data => {
  const {descriptionPage = {}, speedCourseSettings = {}} = data;

  const thumbnails = {};

  // 1. Create a shallow copy of the pages array
  const pages = [...data.pages || []];

  for (const [i, pageEntry] of pages.entries()) {
    // 2. Create a shallow copy of the page object so we don't mutate original data properties
    let page = {...pageEntry};

    if (page.templateTypeId === TEMPLATE_QUESTIONNAIRE && (!page.version || page.version === 1)) {
      page = migrateLegacyQuizData(page);
    }

    // don't include thumbnail url if it is a blob or a local dev url
    if (page?.thumbnailUrl?.startsWith?.('https') || page?.thumbnailUrl?.startsWith?.('/')) {
      thumbnails[page.id] = {
        url: page.thumbnailUrl,
        isFetching: false,
      };
      page.thumbnailRequested = false;
    } else {
      // if no valid thumbnail url, schedule a thumbnail so it will be fetched when needed
      page.thumbnailUrl = undefined;
      page.thumbnailRequested = true;
    }

    pages[i] = page;
  }

  const searchParams = new URLSearchParams(window.location?.search || '');

  return {
    thumbnails,
    ...data,
    descriptionPage: {
      ...descriptionPage,
      bgImage: descriptionPage?.cover === 'UPLOADING' ? undefined : descriptionPage?.cover,
      title: descriptionPage?.title?.text ?? descriptionPage?.title ?? decodeURIComponent(searchParams.get('title') || '') ?? '',
      description: descriptionPage?.description?.text ?? descriptionPage?.description ?? decodeURIComponent(searchParams.get('description') || '') ?? '',
    },
    speedCourseSettings: {
      ...descriptionPage,
      bgImage: speedCourseSettings.cover === 'UPLOADING' ? undefined : speedCourseSettings.cover,
      title: speedCourseSettings.title || '',
      description: speedCourseSettings.description || '',
      useRandomize:
        typeof speedCourseSettings.useRandomize === 'boolean'
          ? speedCourseSettings.useRandomize
          : null,
      useQuestionPool:
        typeof speedCourseSettings.useQuestionPool === 'boolean'
          ? speedCourseSettings.useQuestionPool
          : null,
      questionPoolSize:
        typeof speedCourseSettings.questionPoolSize === 'number'
          ? speedCourseSettings.questionPoolSize
          : null,
      timeToReadQuestionSec:
        typeof speedCourseSettings.timeToReadQuestionSec === 'number'
          ? speedCourseSettings.timeToReadQuestionSec
          : null,
      timeToAnswerQuestionSec:
        typeof speedCourseSettings.timeToAnswerQuestionSec === 'number'
          ? speedCourseSettings.timeToAnswerQuestionSec
          : null,
      useMultipleRetries:
        typeof speedCourseSettings.useMultipleRetries === 'boolean'
          ? speedCourseSettings.useMultipleRetries
          : null,
      pointsMaxScore:
        typeof speedCourseSettings.pointsMaxScore === 'number'
          ? speedCourseSettings.pointsMaxScore
          : null,
      pointsPerAnswer:
        typeof speedCourseSettings.pointsPerAnswer === 'number'
          ? speedCourseSettings.pointsPerAnswer
          : null,
    },

    files: pages.flatMap(page => {
      const files = [];

      switch (page.templateTypeId) {
      case TEMPLATE_PICTURESLIDES: {
        page.pictureslides.slides.forEach(slide => {
          if (slide?.bgImage) {
            files.push(slide.bgImage.url);
          }
          if (slide?.items?.length) {
            slide.items.forEach(item => {
              if (item?.type === 'imageElement' && item.data?.file?.url) {
                files.push(item.data.file.url);
              }
            });
          }
        });
        break;
      }
      default: {
        break;
      }
      }

      return files;
    }),

    pages: pages.map(page => {
      // remove "isProcessingThumbnail" if present (this should not be stored on server)
      delete page.isProcessingThumbnail;

      if (page.redirectPageId) {
        page.gotoPage = {
          ...page.gotoPage,
          pageId: page.redirectPageId,
        };
        delete page.redirectPageId;
      }

      if (page?.questionnaire?.answerMode && !page.answerMode) {
        page.answerMode = page.questionnaire.answerMode;
        delete page.questionnaire.answerMode;
      }

      if (page.id === PAGE_ID_SPEED_COURSE) {
        return page;
      }

      if (page.templateTypeId === TEMPLATE_DILEMMA && page.dilemma) {
        // Unpack dilemma.question.attr into dilemma.question
        page.dilemma.question = {
          ...page.dilemma.question,
          ...page.dilemma.question?.attr,
        };
        // Clean up
        delete page.dilemma.question.attr;
      } else if (page.templateTypeId === TEMPLATE_VIDEO && page.video) {
        // Flatten video.video, video.video.meta into video
        page.video = {
          ...page.video,
          ...page.video?.video,
          ...page.video?.video?.meta,
        };
        page.video.title = page.video.title || {text: page.title};
        page.video.subtitle = page.video.subtitle || page.subtitle;
        // Clean up
        delete page.video.video;
        delete page.video.attr;
        delete page.video.meta;
      }

      return getPageSections(page.templateTypeId).reduce(
        (parsedPage, {dataKey, componentName}) => {
          switch (componentName) {
          case PICTURESLIDES: {
            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                slides: parsedPage[dataKey].slides.map(slide =>
                  slide.image === 'UPLOADING'
                    ? {
                      ...slide,
                      image: undefined,
                    }
                    : slide),
              },
            };
          }
          case CONTENT: {
            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                files: parsedPage[dataKey].files || [],
                redirections: parsedPage[dataKey].redirections || {},
                timeBounds: parsedPage[dataKey].timeBounds || {customTimeBounds: false},
              },
            };
          }
          case QUESTIONNAIRE: {
            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                redirections: parsedPage[dataKey].redirections || {},
                timeBounds: parsedPage[dataKey].timeBounds || {customTimeBounds: false},
              },
            };
          }
          case QUESTIONNAIREINPUT: {
            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                redirections: parsedPage[dataKey].redirections || {},
                timeBounds: parsedPage[dataKey].timeBounds || {customTimeBounds: false},
              },
            };
          }
          case PICTURESQUESTIONNAIRE: {
            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                answers: parsedPage[dataKey].answers.map(answer =>
                  answer.image === 'UPLOADING'
                    ? {
                      ...answer,
                      image: undefined,
                    }
                    : answer),
                redirections: parsedPage[dataKey].redirections || {},
                timeBounds: parsedPage[dataKey].timeBounds || {customTimeBounds: false},
              },
            };
          }
          case VIDEO: {
            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                url:
                    parsedPage[dataKey].url
                    || (parsedPage[dataKey].video?.file === 'UPLOADING'
                      ? undefined
                      : parsedPage[dataKey].video?.file),
                poster:
                    parsedPage[dataKey].poster
                    || (parsedPage[dataKey].video?.poster === 'UPLOADING'
                      ? undefined
                      : parsedPage[dataKey].video?.poster),
              },
            };
          }
          case PICTURE: {
            let file;

            if (parsedPage[dataKey]) {
              file
                  = parsedPage[dataKey].file === 'UPLOADING' ? undefined : parsedPage[dataKey].file;
            }

            return {
              ...parsedPage,
              [dataKey]: {
                ...parsedPage[dataKey],
                file,
              },
            };
          }
          default: {
            return parsedPage;
          }
          }
        },
        {...page},
      );
    }),
  };
};

const pagesTypesNames = {
  [TEMPLATE_PICTURESLIDES]: i18n('pages.types.scene'),
  [TEMPLATE_QUESTIONNAIRE]: i18n('pages.types.question'),
  [TEMPLATE_INPUTQUESTIONNAIRE]: i18n('pages.types.assessment'),
  [TEMPLATE_DILEMMA]: i18n('pages.types.flashcard'),
  [TEMPLATE_CONTENT]: i18n('pages.types.content'),
  [TEMPLATE_VIDEO]: i18n('pages.types.video'),
  [TEMPLATE_PICTUREQUESTIONNAIRE]: i18n('pages.types.picture-scene'),
  [TEMPLATE_EMBED]: i18n('pages.types.embed'),
  PPT: i18n('pages.types.powerpoint'),
};

export const getTemplateTypeName = templateTypeId => {
  if (!templateTypeId) {
    return '';
  }

  return pagesTypesNames[templateTypeId];
};

export const findAndReplace = async (template, bundle) => {
  Object.keys(template).forEach(key => {
    if (typeof template[key] === typeof {}) {
      if (bundle && template) {
        findAndReplace(template[key], bundle[key]);
      }
    } else {
      bundle[key] = template[key];
    }
  });
};

// export const applyTemplateTheme = (theme, font, bundle) => {
//   if (theme && theme.data) {
//     Object.keys(theme.data).forEach(key => {
//       if (key === '[ALL]' || key === bundle.templateTypeId) {
//         if (bundle.templateTypeId === 'PICTURESLIDES-TEXT') {
//           const slide = bundle.pictureslides.slides[0];
//           const themeItem = theme.data[key];

//           findAndReplace(themeItem, slide);

//           if (Object.keys(theme.data[key]).includes('items')) {
//             Object.keys(theme.data[key]['items']).forEach(item => {
//               if (Object.keys(slide).includes('items')) {
//                 slide.items.forEach(nsi => {
//                   if (item === nsi.type || item === nsi?.name) {
//                     findAndReplace(theme.data[key].items[item], nsi);
//                   }
//                 });
//               }
//             });
//           }
//         } else {
//           findAndReplace(theme.data[key], bundle);
//         }
//       }
//     });
//   }

//   bundle.defaultFont = font;
//   bundle.defaultTheme = theme;
// };

export const getTemplateTypeMiniatureUrl = templateTypeId => {
  if (!templateTypeId) {
    return '';
  }

  const templateType = pageTypes.find(pageType => pageType.id === templateTypeId);

  if (!templateType?.miniatureFileName) {
    return '';
  }

  return `${staticsRoot}/static/images/nano/templates-miniatures/${templateType.miniatureFileName}`;
};

export const getSectionComponent = componentName => {
  switch (componentName) {
  case PICTURESLIDES: {
    return PictureSlide;
  }
  case PICTURESQUESTIONNAIRE: {
    return PicturesQuestionnaire;
  }
  case QUESTIONNAIRE: {
    return QuizView;
  }
  case QUESTIONNAIREINPUT: {
    return QuestionnaireInput;
  }
  case VIDEO: {
    return Video;
  }
  case PICTURE: {
    return Picture;
  }
  case DILEMMA: {
    return Dilemma;
  }
  case CONTENT: {
    return Content;
  }
  case EMBED: {
    return Embed;
  }
  default: {
    console.error('No section of this type', componentName);

    return null;
  }
  }
};

export const preloadPages = pages => pages?.forEach?.(page =>
  getPageSections(page.templateTypeId).forEach(({componentName}) => {
    const SectionComponent = getSectionComponent(componentName);

    if (SectionComponent?.preload) {
      SectionComponent.preload();
    }
  }));

// const getPlayerShouldDisplayBottomStatusBar = pageTemplateName => {
//   switch (pageTemplateName) {
//   case TEMPLATE_CONTENT:
//     return true;
//   case TEMPLATE_QUESTIONNAIRE:
//   case TEMPLATE_INPUTQUESTIONNAIRE:
//     return false;
//   case TEMPLATE_PICTUREQUESTIONNAIRE:
//     return true;
//   case TEMPLATE_PICTURESLIDES:
//     return false;
//   case TEMPLATE_VIDEO:
//     return true;
//   case TEMPLATE_DILEMMA:
//     return false;
//   default:
//     return true;
//   }
// };

export const getPlayerShouldDisplayTopStatusBar = pageTemplateName => {
  switch (pageTemplateName) {
  case TEMPLATE_PICTURESLIDES: {
    return false;
  }
  case TEMPLATE_DILEMMA: {
    return false;
  }
  case TEMPLATE_QUESTIONNAIRE:
  case TEMPLATE_INPUTQUESTIONNAIRE: {
    return false;
  }
  default: {
    return false;
  }
  }
};

export const getTopStatusBarVariant = pageTemplateName => {
  switch (pageTemplateName) {
  case TEMPLATE_QUESTIONNAIRE: {
    return 'FULLSCREEN';
  }
  default: {
    return 'CENTERED';
  }
  }
};

// const getSectionComponentInitialValues = componentName => {
//   switch (componentName) {
//   case PICTURESLIDES:
//     return {slides: [createSlide()]};
//   case PICTURESQUESTIONNAIRE:
//     return {
//       question: '',
//       answers: [
//         {
//           id: getIdForNextElement([]),
//           isCorrect: true,
//         },
//       ],
//       settings: {
//         dndSelect: false,
//         pointsEnabled: true,
//         pointsPerAnswer: 1,
//         pointsMaxScore: 1,
//       },
//       texts: {
//         textWhenGood: '',
//         textWhenWrong: '',
//       },
//       redirections: {},
//       timeBounds: {customTimeBounds: false},
//     };
//   case DILEMMA:
//     return {
//       answer: {
//         text: 'Write a long topic',
//         color: '#fff',
//         bgColor: 'rgba(0,0,0,0.6)',
//       },
//       question: {
//         text: 'Write your dilemma',
//         color: '#fff',
//       },
//     };
//   case QUESTIONNAIRE:
//     return {
//       question: '',
//       answers: [
//         {
//           id: getIdForNextElement([]),
//           isCorrect: true,
//           text: 'Enter answer',
//         },
//       ],
//       settings: {
//         pointsEnabled: true,
//         pointsPerAnswer: 1,
//         pointsMaxScore: 1,
//       },
//       texts: {
//         textWhenGood: '',
//         textWhenWrong: '',
//       },
//       redirections: {},
//       timeBounds: {customTimeBounds: false},
//     };
//   case QUESTIONNAIREINPUT:
//     return {
//       question: '',
//       answers: [
//         {
//           id: getIdForNextElement([]),
//           isCorrect: true,
//           text: '',
//         },
//       ],
//       input: [
//         {
//           type: 0,
//           range: [0, 5],
//         },
//       ],
//       texts: {
//         textWhenGood: '',
//         textWhenWrong: '',
//       },
//       redirections: {},
//     };
//   case CONTENT:
//     return {
//       htmlValue: '',
//       files: [],
//     };
//   case VIDEO:
//     return {
//       video: {
//         autoPlay: false,
//         file: undefined,
//         meta: {
//           title: '',
//           subtitle: '',
//         },
//       },
//     };
//   case PICTURE:
//     return {file: undefined};
//   default:
//     return {};
//   }
// };

const getPageComponentInitialValues = componentName => {
  switch (componentName) {
  case TEMPLATE_PICTURESLIDES: {
    return {pictureslides: {slides: [createSlide()]}};
  }
  case PICTURESQUESTIONNAIRE: {
    return {
      question: '',
      answers: [
        {
          id: getIdForNextElement([]),
          isCorrect: true,
        },
      ],
      settings: {
        dndSelect: false,
        pointsEnabled: true,
        pointsPerAnswer: 1,
        pointsMaxScore: 1,
      },
      texts: {
        textWhenGood: '',
        textWhenWrong: '',
      },
      redirections: {},
      timeBounds: {customTimeBounds: false},
    };
  }
  case TEMPLATE_DILEMMA: {
    return {
      dilemma: {
        answer: {
          text: 'Write a long topic',
          color: '#fff',
          bgColor: 'rgba(0,0,0,0.6)',
        },
        question: {
          text: '',
          color: '#fff',
        },
      },
      bgColor: '#007AFF',
      justifyContent: 'flex-end',
    };
  }
  case TEMPLATE_QUESTIONNAIRE: {
    return {
      title: '',
      text: {
        defaultValue: i18n('placeholder.text'),
        isVisible: false,
      },
      questionnaire: {
        question_type: QuestionType.MULTIPLE_CHOICE,
        question: '',
        answers: [
          {
            id: crypto.randomUUID(),
            isCorrect: true,
            text: '',
          },
        ],
        textWhenGood: '',
        textWhenWrong: '',
      },
      txtColor: null,
      version: 2,
    };
  }
  case TEMPLATE_INPUTQUESTIONNAIRE: {
    return {
      title: '',
      text: {
        defaultValue: i18n('placeholder.text'),
        paragraphValue: '',
        isVisible: false,
      },
      questionnaireinput: {
        question: '',
        answers: [
          {
            id: getIdForNextElement([]),
            isCorrect: true,
            text: '',
          },
        ],
        input: {type: 'user-input'},
        texts: {
          textWhenGood: '',
          textWhenWrong: '',
        },
        redirections: {},
      },
    };
  }
  case CONTENT: {
    return {
      htmlValue: '',
      files: [],
    };
  }
  case TEMPLATE_VIDEO: {
    return {
      bgColor: '#1C1C1E',
      video: {
        autoPlay: false,
        file: undefined,
        meta: {
          title: '',
          subtitle: '',
        },
      },
    };
  }
  case TEMPLATE_EMBED: {
    return {
      width: 3,
      justifyContent: 'center',
      lightStatusbar: null,
      lightNavigationButtons: null,
      embed: {
        code: '',
        horizontalMargin: 0,
        verticalMargin: 0,
        horizontalPadding: 0,
        verticalPadding: 0,
        borderRadius: 0,
        bgColor: 'rgba(255,255,255,0)',
      },
    };
  }
  case PICTURE: {
    return {file: undefined};
  }
  default: {
    return {};
  }
  }
};

export const getPageInitialValues = ({id, templateTypeId}) => {
  const pageType = pageTypes.find(i => i.id === templateTypeId);

  if (!pageType) {
    return {};
  }

  const newPage = {
    id,
    templateTypeId,
    title: `${getTemplateTypeName(templateTypeId)}`.trim(),
    ...getPageComponentInitialValues(templateTypeId),
  };

  return newPage;
};

export const getTemplateTypeShape = () =>
  PropTypes.shape({
    id: PropTypes.string.isRequired,
    sections: PropTypes.arrayOf(PropTypes.shape({
      componentName: PropTypes.oneOf([
        PICTURESLIDES,
        PICTURESQUESTIONNAIRE,
        QUESTIONNAIRE,
        CONTENT,
        TEXT,
        VIDEO,
      ]).isRequired,
      dataKey: PropTypes.string.isRequired,
    })),
  });

export const getRedirectPages = (currentPage, allPages) => [
  {id: null},
  ...allPages.filter(p => p.id !== currentPage.id),
];

/**
 * adjust the color of text element so the contrast between text and background is preserved
 */
export const adjustTxtColorTobgColor = items => items.map(item =>
  item.type === 'textElement'
    ? {
      ...item,
      data: {
        ...item.data,
        color: null,
      },
    }
    : item);

export const defaultCustomBgColor = '#2e77eb';
